<script setup lang="ts">
import { computed, ref } from 'vue';
import config from '@/config/ConfigProvider';
import { useLocale } from '@/plugins/translation';
import HorizontalContentList from '@/components/collections/HorizontalContentList.vue';
import SearchInput from '@/components/collections/SearchInput.vue';
import {
  useContentsSharedWithMe,
  useMyMemberCollections,
  useMySubscribedCollections,
} from '@ilteducation/content-collections';

const { t, l } = useLocale();
const context = config.value('context');

const searchValue = ref<string>('');

const { collections: memberCollections, isLoading: isMemberLoading } = useMyMemberCollections(context, 100, undefined);
const { collections: subscribedCollections, isLoading: isSubscribedLoading } = useMySubscribedCollections(context, 100);

const isLoading = computed(() => isMemberLoading.value || isSubscribedLoading.value);

const contentsSharedWithMeParams = computed(() => ({
  context: context,
}));

const { collection: sharedContentsCollection } = useContentsSharedWithMe(contentsSharedWithMeParams);

const allCollections = computed(() => {
  const collections = [];

  // Add "shared with me" virtual collection if it has content
  if (
    sharedContentsCollection.value &&
    sharedContentsCollection.value.contents.type === 'ContentsByReferences' &&
    sharedContentsCollection.value.contents.references.length > 0
  ) {
    collections.push(sharedContentsCollection.value);
  }

  // Add member collections (collections where user is added as a member/collaborator)
  collections.push(...memberCollections.value);

  // Add subscribed collections (collections user has followed)
  collections.push(...subscribedCollections.value);

  return collections;
});

const filteredCollections = computed(() => {
  if (!searchValue.value.trim()) {
    return allCollections.value.slice().sort((a, b) => a.name.localeCompare(b.name));
  }
  return allCollections.value
    .filter((collection) => {
      return collection.name.toLowerCase().includes(searchValue.value.toLowerCase());
    })
    .sort((a, b) => a.name.localeCompare(b.name));
});

const onSearch = (value: string) => {
  searchValue.value = value;
};

const onClearSearch = () => {
  searchValue.value = '';
};
</script>

<template>
  <v-container class="custom-container pa-4">
    <v-row class="mb-4">
      <v-col cols="8">
        <SearchInput @do-search="onSearch" @clear-search="onClearSearch"></SearchInput>
      </v-col>
    </v-row>

    <v-row v-if="isLoading && allCollections.length === 0">
      <v-col>
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>

    <v-row v-for="collection in filteredCollections" :key="collection.id" no-gutters class="flex-column">
      <v-col>
        <div class="d-flex align-center">
          <h2 class="collection-title mr-8">{{ collection.name }}</h2>
          <v-btn color="on-surface-medium" variant="text" class="text-none" :to="`collection/${collection.id}`">
            {{ t(l.show_more) }}
            <template #append>
              <v-icon icon="fa-regular fa-chevron-right" />
            </template>
          </v-btn>
        </div>
      </v-col>
      <v-col class="pl-2 pt-2">
        <HorizontalContentList :collection="collection" />
      </v-col>
    </v-row>

    <template v-if="filteredCollections.length === 0 && searchValue">
      {{ t(l.label_no_matches) }}
    </template>
    <template v-if="!isLoading && allCollections.length === 0 && !searchValue">
      {{ t(l.content_collections.no_shared_collections) }}
    </template>
  </v-container>
</template>

<style scoped lang="scss">
.collection-title {
  color: rgb(var(--v-theme-on-surface-medium));
  font-weight: 500;
}
</style>
